import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { buildCharges, computeBill, isItemInSchedule, priceLine, supplyTypeFor } from '@shripos/shared/billing';
import { cachedGet } from './offline/cache.js';
import { useSession } from '../stores/session.js';

/**
 * The outlet's POS catalog (menu, prices, settings). Live-refreshed via socket 'menu:updated';
 * the last copy is kept on the device so billing works offline.
 */
export const catalogKey = () => `catalog:${useSession.getState().me?.outlet?.id ?? 'none'}`;
export const fetchCatalog = () => cachedGet('/pos/catalog', catalogKey());

export function useCatalog(enabled = true) {
  return useQuery({
    queryKey: ['catalog'],
    queryFn: fetchCatalog,
    staleTime: 5 * 60_000,
    enabled,
    networkMode: 'always',
  });
}

export function useCatalogMaps(catalog) {
  return useMemo(() => {
    if (!catalog) return null;
    return {
      itemsById: new Map(catalog.items.map((i) => [i.id, i])),
      modifierGroupsById: new Map(catalog.modifierGroups.map((g) => [g.id, g])),
      taxGroupsById: new Map(catalog.taxGroups.map((t) => [t.id, t])),
      schedulesById: new Map(catalog.schedules.map((s) => [s.id, s])),
      categoriesById: new Map(catalog.categories.map((c) => [c.id, c])),
    };
  }, [catalog]);
}

export function itemAvailableNow(item, maps, tz, now = new Date()) {
  return isItemInSchedule(item, maps.schedulesById, now, tz);
}

/** Lowest current price of an item (for tiles): its own price or its cheapest active variant. */
export function fromPrice(item) {
  if (item.variants?.length) return Math.min(...item.variants.map((v) => v.pricePaise));
  return item.pricePaise;
}

/**
 * Live cart totals with the shared engine — the same code the server runs, so the total on screen
 * is the total that gets billed. Lines that can't be priced (e.g. item went out of stock) are
 * reported in `errors` and left out of the totals.
 */
export function usePricedCart(cart, catalog, maps, coupon = null) {
  return useMemo(() => {
    if (!catalog || !maps) return null;
    const priced = [];
    const errors = {};
    for (const l of cart.lines) {
      const sent = cart.sentQty?.[l.lineId] ?? 0;
      let item = maps.itemsById.get(l.itemId);
      if (item && l.qty <= sent)
        item = { ...item, outOfStock: false, variants: item.variants.map((v) => ({ ...v, outOfStock: false })) };
      const r = priceLine(item, l, maps);
      if (r.ok) priced.push(r);
      else errors[l.lineId] = r.error;
    }
    const discount = (d) =>
      d
        ? d.type === 'percent'
          ? { type: 'percent', valueBp: d.valueBp }
          : { type: 'flat', valuePaise: d.valuePaise }
        : null;
    const chargeDefs = buildCharges(catalog.outlet.pos, cart.orderType, maps.taxGroupsById);
    const calc = computeBill({
      lines: priced.map((p) => p.line),
      billDiscount: discount(cart.billDiscount),
      coupon,
      charges: chargeDefs,
      supply: supplyTypeFor(catalog.outlet.stateCode, cart.customer?.gstin),
      rounding: catalog.settings.rounding,
    });
    const byId = new Map(priced.map((p, i) => [p.line.lineId, { ...p, amounts: calc.lines[i].amounts }]));
    return { ...calc, byId, errors, chargeDefs };
  }, [cart, catalog, maps, coupon]);
}
